import React, { useState } from "react";

export const fmtEur = (n) => "€" + Math.round(n).toLocaleString("es-ES");
export const fmtEurK = (n) => {
  if (n >= 1000000) return "€" + (n / 1000000).toLocaleString("es-ES", { maximumFractionDigits: 1 }) + "M";
  if (n >= 1000) return "€" + Math.round(n / 1000) + "k";
  return "€" + n;
};
export const fmtN = (n) => Math.round(n).toLocaleString("es-ES");
export const fmtPct = (n, dec = 1) => (n == null || isNaN(n) ? "—" : n.toFixed(dec).replace(".", ",") + "%");

/* Bars + line (current bars, expense bars, previous-year line) */
export const BarLineChart = ({ months, cur, prev, expenses, curProj, expProj }) => {
  const W = 760, H = 260, padL = 36, padR = 18, padT = 16, padB = 28;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const projVals = [...(curProj || []), ...(expProj || [])].filter((v) => v != null);
  const allVals = [...cur, ...(prev || []), ...(expenses || []), ...projVals];
  const max = (Math.max(...allVals) || 1) * 1.15;
  const scaleX = (i) => padL + (innerW / months.length) * (i + 0.5);
  const scaleY = (v) => padT + innerH - (v / max) * innerH;
  const barW = (innerW / months.length) * 0.32;
  const gap = 2;
  const [hover, setHover] = useState(null);

  return (
    <div style={{ position: "relative" }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: "block", overflow: "visible" }}>
        {[0, 0.25, 0.5, 0.75, 1].map((t, i) => {
          const y = padT + innerH * (1 - t);
          const val = Math.round(max * t);
          return (
            <g key={i}>
              <line x1={padL} x2={W - padR} y1={y} y2={y} stroke="var(--ce-border-subtle)" strokeDasharray="2 3" />
              <text x={padL - 8} y={y + 3} textAnchor="end" fontSize="10" fill="var(--ce-fg-faint)">{fmtEurK(val)}</text>
            </g>
          );
        })}
        {prev ? (
          <path
            d={prev.map((v, i) => `${i === 0 ? "M" : "L"} ${scaleX(i)} ${scaleY(v)}`).join(" ")}
            fill="none" stroke="var(--ce-chart-previous)" strokeWidth="1.8" strokeDasharray="3 4"
          />
        ) : null}
        {months.map((m, i) => {
          const xc = scaleX(i);
          const cy = scaleY(cur[i] || 0);
          const ey = expenses ? scaleY(expenses[i] || 0) : null;
          const pcv = curProj && curProj[i] != null ? curProj[i] : null;
          const pev = expProj && expProj[i] != null ? expProj[i] : null;
          const pcy = pcv != null ? scaleY(pcv) : null;
          const pey = pev != null ? scaleY(pev) : null;
          return (
            <g key={m} onMouseEnter={() => setHover({ i, m })} onMouseLeave={() => setHover(null)}>
              {/* Proyección: contorno punteado, el relleno real queda dentro */}
              {pcy != null ? (
                <rect x={xc - barW - gap} y={pcy} width={barW} height={padT + innerH - pcy} rx="3"
                  fill="var(--ce-chart-1)" fillOpacity="0.1" stroke="var(--ce-chart-1)" strokeWidth="1.2" strokeDasharray="3 3" />
              ) : null}
              {pey != null ? (
                <rect x={xc + gap} y={pey} width={barW} height={padT + innerH - pey} rx="3"
                  fill="var(--ce-chart-6)" fillOpacity="0.1" stroke="var(--ce-chart-6)" strokeWidth="1.2" strokeDasharray="3 3" />
              ) : null}
              <rect x={xc - barW - gap} y={cy} width={barW} height={padT + innerH - cy} rx="3" fill="var(--ce-chart-1)" />
              {expenses ? (
                <rect x={xc + gap} y={ey} width={barW} height={padT + innerH - ey} rx="3" fill="var(--ce-chart-6)" />
              ) : null}
              <text x={xc} y={H - 6} textAnchor="middle" fontSize="10" fill="var(--ce-fg-faint)">{m}</text>
              {hover && hover.i === i ? <line x1={xc} x2={xc} y1={padT} y2={padT + innerH} stroke="var(--ce-border-strong)" strokeDasharray="2 3" /> : null}
            </g>
          );
        })}
      </svg>
      {hover ? (
        <div className="tt" style={{ left: `${(scaleX(hover.i) / W) * 100}%`, top: 8, transform: "translateX(-50%)" }}>
          <div className="tt-t">{hover.m}</div>
          <div className="tt-r"><span className="d" style={{ background: "var(--ce-chart-1)" }} />Actual: {fmtEur(cur[hover.i] || 0)}</div>
          {expenses ? <div className="tt-r"><span className="d" style={{ background: "var(--ce-chart-6)" }} />Gastos: {fmtEur(expenses[hover.i] || 0)}</div> : null}
          {prev ? <div className="tt-r"><span className="d" style={{ background: "var(--ce-chart-previous)" }} />Anterior: {fmtEur(prev[hover.i] || 0)}</div> : null}
          {curProj && curProj[hover.i] != null ? <div className="tt-r"><span className="d" style={{ background: "var(--ce-chart-1)", opacity: 0.5 }} />Proy. ingresos: {fmtEur(curProj[hover.i])}</div> : null}
          {expProj && expProj[hover.i] != null ? <div className="tt-r"><span className="d" style={{ background: "var(--ce-chart-6)", opacity: 0.5 }} />Proy. gastos: {fmtEur(expProj[hover.i])}</div> : null}
        </div>
      ) : null}
    </div>
  );
};

export const GroupedBarChart = ({ months, series }) => {
  const W = 760, H = 260, padL = 36, padR = 18, padT = 16, padB = 28;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const max = (Math.max(...series.flatMap((s) => s.values)) || 1) * 1.15;
  const groupW = innerW / months.length;
  const barW = (groupW * 0.7) / series.length;
  const [hover, setHover] = useState(null);
  const scaleY = (v) => padT + innerH - (v / max) * innerH;

  return (
    <div style={{ position: "relative" }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: "block", overflow: "visible" }}>
        {[0, 0.25, 0.5, 0.75, 1].map((t, i) => {
          const y = padT + innerH * (1 - t);
          const val = Math.round(max * t);
          return (
            <g key={i}>
              <line x1={padL} x2={W - padR} y1={y} y2={y} stroke="var(--ce-border-subtle)" strokeDasharray="2 3" />
              <text x={padL - 8} y={y + 3} textAnchor="end" fontSize="10" fill="var(--ce-fg-faint)">{fmtN(val)}</text>
            </g>
          );
        })}
        {months.map((m, i) => {
          const gx = padL + groupW * i + groupW / 2;
          return (
            <g key={m} onMouseEnter={() => setHover({ i, m })} onMouseLeave={() => setHover(null)}>
              {series.map((s, si) => {
                const v = s.values[i] || 0;
                const x = gx - (barW * series.length) / 2 + barW * si + 1;
                const y = scaleY(v);
                return <rect key={si} x={x} y={y} width={barW - 2} height={padT + innerH - y} rx="3" fill={s.color} opacity={s.muted ? 0.5 : 1} />;
              })}
              <text x={gx} y={H - 6} textAnchor="middle" fontSize="10" fill="var(--ce-fg-faint)">{m}</text>
            </g>
          );
        })}
      </svg>
      {hover ? (
        <div className="tt" style={{ left: `${((padL + groupW * hover.i + groupW / 2) / W) * 100}%`, top: 8, transform: "translateX(-50%)" }}>
          <div className="tt-t">{hover.m}</div>
          {series.map((s, si) => (
            <div className="tt-r" key={si}><span className="d" style={{ background: s.color }} />{s.label}: {fmtN(s.values[hover.i] || 0)}</div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export const MultiLineChart = ({ months, series }) => {
  const W = 760, H = 260, padL = 36, padR = 18, padT = 16, padB = 28;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const max = (Math.max(...series.flatMap((s) => s.values)) || 1) * 1.15;
  const scaleX = (i) => padL + (innerW / (months.length - 1 || 1)) * i;
  const scaleY = (v) => padT + innerH - (v / max) * innerH;
  const [hover, setHover] = useState(null);

  return (
    <div style={{ position: "relative" }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: "block", overflow: "visible" }}>
        <defs>
          <linearGradient id="curFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={series[0]?.color || "#2A5BFF"} stopOpacity="0.18" />
            <stop offset="1" stopColor={series[0]?.color || "#2A5BFF"} stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0, 0.25, 0.5, 0.75, 1].map((t, i) => {
          const y = padT + innerH * (1 - t);
          const val = Math.round(max * t);
          return (
            <g key={i}>
              <line x1={padL} x2={W - padR} y1={y} y2={y} stroke="var(--ce-border-subtle)" strokeDasharray="2 3" />
              <text x={padL - 8} y={y + 3} textAnchor="end" fontSize="10" fill="var(--ce-fg-faint)">{fmtN(val)}</text>
            </g>
          );
        })}
        {months.map((m, i) => (
          <text key={m} x={scaleX(i)} y={H - 6} textAnchor="middle" fontSize="10" fill="var(--ce-fg-faint)">{m}</text>
        ))}
        {series.map((s, si) => {
          const path = s.values.map((v, i) => `${i === 0 ? "M" : "L"} ${scaleX(i)} ${scaleY(v)}`).join(" ");
          return (
            <g key={si}>
              {si === 0 ? <path d={`${path} L ${scaleX(months.length - 1)} ${padT + innerH} L ${scaleX(0)} ${padT + innerH} Z`} fill="url(#curFill)" /> : null}
              <path d={path} fill="none" stroke={s.color} strokeWidth="2" strokeDasharray={s.dashed ? "4 4" : "0"} />
              {s.values.map((v, i) => <circle key={i} cx={scaleX(i)} cy={scaleY(v)} r="2.5" fill={s.color} />)}
            </g>
          );
        })}
        {months.map((m, i) => (
          <rect key={i} x={scaleX(i) - 12} y={padT} width="24" height={innerH} fill="transparent" onMouseEnter={() => setHover({ i, m })} onMouseLeave={() => setHover(null)} />
        ))}
      </svg>
      {hover ? (
        <div className="tt" style={{ left: `${(scaleX(hover.i) / W) * 100}%`, top: 8, transform: "translateX(-50%)" }}>
          <div className="tt-t">{hover.m}</div>
          {series.map((s, si) => (
            <div className="tt-r" key={si}><span className="d" style={{ background: s.color }} />{s.label}: {fmtN(s.values[hover.i] || 0)}</div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export const DonutChart = ({ data, totalLabel = "Total", totalValueFmt = fmtEur, size = 200 }) => {
  const total = data.reduce((a, b) => a + b.value, 0) || 1;
  const R = size / 2;
  const inner = R - 18;
  const cx = R, cy = R;
  let acc = 0;
  const [hover, setHover] = useState(null);

  const arc = (start, end) => {
    const a0 = (start / total) * Math.PI * 2 - Math.PI / 2;
    const a1 = (end / total) * Math.PI * 2 - Math.PI / 2;
    const large = end - start > total / 2 ? 1 : 0;
    const x0 = cx + R * Math.cos(a0), y0 = cy + R * Math.sin(a0);
    const x1 = cx + R * Math.cos(a1), y1 = cy + R * Math.sin(a1);
    const xi1 = cx + inner * Math.cos(a1), yi1 = cy + inner * Math.sin(a1);
    const xi0 = cx + inner * Math.cos(a0), yi0 = cy + inner * Math.sin(a0);
    return `M ${x0} ${y0} A ${R} ${R} 0 ${large} 1 ${x1} ${y1} L ${xi1} ${yi1} A ${inner} ${inner} 0 ${large} 0 ${xi0} ${yi0} Z`;
  };

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
        {data.map((s, i) => {
          const d = arc(acc, acc + s.value);
          acc += s.value;
          return <path key={i} d={d} fill={s.color} stroke="#fff" strokeWidth="1.5" style={{ transition: "transform 180ms var(--ce-ease)", transformOrigin: `${cx}px ${cy}px`, transform: hover === i ? "scale(1.03)" : "scale(1)" }} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} />;
        })}
        <text x={cx} y={cy - 2} textAnchor="middle" fontSize="11" fill="var(--ce-fg-muted)">{hover != null ? data[hover].label.slice(0, 20) : totalLabel}</text>
        <text x={cx} y={cy + 16} textAnchor="middle" fontSize="18" fontWeight="600" fill="var(--ce-fg)">{hover != null ? totalValueFmt(data[hover].value) : totalValueFmt(total)}</text>
      </svg>
    </div>
  );
};

export const DonutLegend = ({ data, totalForPct }) => {
  const total = totalForPct || data.reduce((a, b) => a + b.value, 0) || 1;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1, minWidth: 0 }}>
      {data.map((d, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: "16px 1fr auto", alignItems: "center", gap: 8, fontSize: 12 }}>
          <span style={{ width: 10, height: 10, borderRadius: 999, background: d.color, display: "inline-block" }} />
          <span style={{ color: "var(--ce-fg-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.label}</span>
          <span style={{ color: "var(--ce-fg-muted)", fontVariantNumeric: "tabular-nums" }}>{((d.value / total) * 100).toFixed(1)}%</span>
        </div>
      ))}
    </div>
  );
};

export const Funnel = ({ data }) => {
  const max = Math.max(...data.map((d) => d.value)) || 1;
  const palette = ["#2A5BFF", "#0AB4D4", "#10B981", "#14B8A6"];
  return (
    <div className="funnel">
      {data.map((d, i) => (
        <div className="funnel-row" key={i}>
          <span className="lab">{d.label}</span>
          <div className="bar" style={{ width: `${Math.max(20, (d.value / max) * 100)}%`, background: palette[i % palette.length] }}>
            {fmtN(d.value)} ({d.pct})
          </div>
          <span className="pct">{d.pct}</span>
        </div>
      ))}
    </div>
  );
};

export const Sparkline = ({ values, color = "var(--ce-primary)", w = 88, h = 32, responsive = false }) => {
  if (!values || !values.length) return null;
  const max = Math.max(...values), min = Math.min(...values);
  const range = max - min || 1;
  const path = values.map((v, i) => `${i === 0 ? "M" : "L"} ${(i / (values.length - 1 || 1)) * w} ${h - ((v - min) / range) * (h - 4) - 2}`).join(" ");
  const svgProps = responsive
    ? { viewBox: `0 0 ${w} ${h}`, width: "100%", height: h, preserveAspectRatio: "none", style: { display: "block" } }
    : { viewBox: `0 0 ${w} ${h}`, width: w, height: h };
  return (
    <svg {...svgProps}>
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
      <circle cx={w} cy={h - ((values[values.length - 1] - min) / range) * (h - 4) - 2} r="2.5" fill={color} />
    </svg>
  );
};

export const HorizontalBarList = ({ data, max: maxOverride, fmt = fmtN }) => {
  const max = maxOverride || Math.max(...data.map((d) => d.value)) || 1;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {data.map((d, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 80px", gap: 10, alignItems: "center", fontSize: 12 }}>
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, color: "var(--ce-fg-2)" }}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.label}</span>
              <span style={{ color: "var(--ce-fg-muted)", fontVariantNumeric: "tabular-nums" }}>{fmt(d.value)}</span>
            </div>
            <div style={{ height: 6, background: "var(--ce-bg-2)", borderRadius: 4, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${(d.value / max) * 100}%`, background: d.color || "var(--ce-primary)", borderRadius: 4, transition: "width 320ms var(--ce-ease)" }} />
            </div>
          </div>
          <span style={{ color: "var(--ce-fg-muted)", textAlign: "right", fontFamily: "var(--ce-font-mono)" }}>{d.pct || ((d.value / max) * 100).toFixed(1) + "%"}</span>
        </div>
      ))}
    </div>
  );
};
